<template>
    <div class="w-full h-full flex flex-col">
        <!-- AceEditor (Takes Full Height) -->
        <VAceEditor ref="editorRef" class="w-full flex-grow border custom-ace" v-model:value="localEditedText"
            lang="json" :theme="editorStore.theme" :options="editorOptions" @change="onEditorChange" />

        <!-- Validation errors (blocking) -->
        <div v-if="exportValidationErrors.length > 0" class="mt-2 rounded border border-red-500/40 bg-red-900/30 px-3 py-2 text-xs text-red-300 space-y-2">
            <template v-for="group in validationErrorGroups" :key="group.category">
                <div>
                    <div class="font-semibold text-red-400">{{ group.title }}:</div>
                    <div v-for="(err, i) in group.errors" :key="i">{{ err }}</div>
                </div>
            </template>
        </div>

        <!-- Export warnings (non-blocking) -->
        <div v-if="exportWarnings.length > 0" class="mt-2 rounded border border-amber-500/40 bg-amber-900/30 px-3 py-2 text-xs text-amber-300 space-y-1">
            <div class="font-semibold text-amber-400">Warnings:</div>
            <div v-for="(warn, i) in exportWarnings" :key="i">{{ warn }}</div>
        </div>

        <!-- Button (Separate, No Scrollbars) -->
        <div class="mt-4 flex items-center space-x-2">
            <form-button v-if="props.settings.tauri" class="w-1/3" :disabled="isSending || isDeploying" @click="sendToBroker">
                Send to {{ settings.plate.name }}
            </form-button>
            <form-button v-if="props.settings.tauri" class="w-1/3" :disabled="isSending || isDeploying" @click="showDeployConfirm = true">
                Deploy to {{ settings.plate.name }}
            </form-button>
            <form-button :class="props.settings.tauri ? 'w-1/3' : 'w-full'" @click="openModal">
                Save to file
            </form-button>
            <AceThemeSelector class="ml-auto flex-shrink-0" />
        </div>
        <!-- Deploy confirmation balloon -->
        <div v-if="showDeployConfirm" class="mt-2 bg-white/15 border border-white/20 rounded shadow p-3">
            <p class="text-sm font-medium text-white">Deploy <strong>pages.jsonl</strong> to {{ settings.plate.name }}?</p>
            <div class="flex space-x-2 mt-2">
                <form-button color="danger" class="w-16" @click="confirmDeploy">Yes</form-button>
                <form-button class="w-16" @click="showDeployConfirm = false">No</form-button>
            </div>
        </div>
        <!-- Reconnect progress -->
        <div v-if="isReconnecting" class="mt-2">
            <div class="flex justify-between text-xs text-ui-active mb-1">
                <span>Reconnecting to broker...</span>
            </div>
            <div class="w-full bg-white/15 rounded-full h-2">
                <div class="bg-ui-active h-2 rounded-full animate-pulse w-full" />
            </div>
        </div>
        <!-- Transfer progress bar -->
        <div v-if="isSending" class="mt-2">
            <div class="flex justify-between text-xs text-slate-400 mb-1">
                <span>Sending to {{ settings.plate.name }}...</span>
                <span>{{ sendProgress }}%</span>
            </div>
            <div class="w-full bg-white/15 rounded-full h-2">
                <div class="bg-blue-500 h-2 rounded-full transition-all duration-150"
                    :style="{ width: sendProgress + '%' }" />
            </div>
        </div>
        <!-- Deploy progress -->
        <div v-if="isDeploying" class="mt-2">
            <div class="flex justify-between text-xs text-green-600 mb-1">
                <span>{{ deployStatus }}</span>
            </div>
            <div class="w-full bg-white/15 rounded-full h-2">
                <div class="bg-green-500 h-2 rounded-full animate-pulse w-full" />
            </div>
        </div>
        <!-- Deploy status message -->
        <div v-if="deployStatus && !isDeploying" class="mt-2 text-xs" :class="deployStatus.startsWith('Deploy failed') ? 'text-red-500' : 'text-green-600'">
            {{ deployStatus }}
        </div>
        <!-- Modal to get the filename -->
        <FilePrompt v-model:filenamePreset="userFilename" @save="saveToFile" :visible="isModalVisible"
            @update:visible="isModalVisible = false" />
    </div>
</template>

<script setup>
import { ref, computed, watchEffect, watch } from "vue";
import { debounce } from "lodash";
import { VAceEditor } from "vue3-ace-editor";
import FilePrompt from './FilePrompt.vue';
import AceThemeSelector from '../AceThemeSelector.vue';
import { useEditorStore } from '../../stores/editorStore';
import { useUndoStore } from '../../history/undoStore';
import JSZip from 'jszip';
import "ace-builds/src-noconflict/mode-json";
import workerJsonUrl from 'ace-builds/src-noconflict/worker-json?url';
import "/src/ace-config";

const editorStore = useEditorStore();
const undoStore = useUndoStore();
import { prepareFirmwareExport } from './prepareFirmwareExport';
import mqttService from '../../services/MqttService';
import { bytesToBase64, uploadDeviceFile } from "../../services/httpTransfer";
import { buildUsedH3dResourceArtifacts } from "../../services/h3dResourcePipeline";

// Props
const props = defineProps({
    popupState: Boolean, // Detect when tab opens
    settings: {
        type: Object,
        required: true,
    },
    layer: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    layerUpdate: {
        type: Number,
        required: true,
    },
});

// Reactive Data
const editorRef = ref(null);
const localEditedText = ref("");
const exportValidationErrors = ref([]);
const exportValidationErrorCategories = ref([]);
const exportWarnings = ref([]);
const isModalVisible = ref(false);
const isSending = ref(false);
const isReconnecting = ref(false);
const sendProgress = ref(0);
const editorOptions = ref({
    fontSize: 12,
    // useWorker: true,
});
// ace.config.setModuleUrl('ace/mode/json_worker', workerJsonUrl);
const userFilename = ref(props.settings.plate.name);

const updateEditor = debounce(() => {
    const prepared = prepareFirmwareExport(props.settings, props.layer);
    exportValidationErrors.value = prepared.validationErrors;
    exportValidationErrorCategories.value = prepared.validationErrorCategories;
    exportWarnings.value = prepared.warnings;
    const jsonl = prepared.jsonl;
    if (typeof jsonl === 'string') {
        localEditedText.value = jsonl;
    } else {
        localEditedText.value = 'no data';
    }
}, 100); // Adjust the debounce delay as necessary

const validationErrorGroups = computed(() => {
    const groups = new Map();
    exportValidationErrors.value.forEach((error, index) => {
        const category = exportValidationErrorCategories.value[index] ?? 'general';
        const title = category === 'template'
            ? 'Export blocked — template errors'
            : category === 'dataset'
                ? 'Export blocked — dataset errors'
                : 'Export blocked — errors';
        if (!groups.has(category)) groups.set(category, { category, title, errors: [] });
        groups.get(category).errors.push(error);
    });
    return [...groups.values()];
});

// Watch for popup open and preload AceEditor text
watchEffect(() => {
    if (props.popupState) {
        // Connect to MQTT broker only once
        if (props.settings.tauri) {
            if (!mqttService.isConnected()) {
                mqttService.connectToBroker(props.settings);
            }
        }
        updateEditor();
    }
});


// Watch for layerUpdate changes
watch(() => props.layerUpdate, (newVal) => {
    if (newVal) {
        updateEditor();
    }
});

// Logic/HA flow edits and undo/redo bypass layerCallback; history catches them.
watch(() => undoStore.historyVersion, updateEditor);

// Handle the editor text change
const onEditorChange = (newValue) => {
    const editor = editorRef.value?.editor; // Check if the editor is available
    if (!editor) return; // Ensure the editor is available before proceeding
    const session = editor.getSession();
    const changedLines = [];

    // Compare the new content with the previous text to detect changed lines
    const diff = compareText(previousText.value, newValue);

    diff.forEach((lineNumber) => {
        changedLines.push(lineNumber);
        // You could add annotation or styling to the gutter for changed lines
        session.addGutterDecoration(lineNumber, 'changed-line');
    });

    previousText.value = newValue; // Update the previous text for next comparison
};

// Function to compare the previous and new text to determine changed lines
const compareText = (oldText, newText) => {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const changedLines = [];

    // Compare line by line
    for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
        if (oldLines[i] !== newLines[i]) {
            changedLines.push(i); // Line number of change
        }
    }
    return changedLines;
};

const sendToBroker = async () => {
    try {
        if (!mqttService.isConnected()) {
            isReconnecting.value = true;
            await mqttService.connectToBroker(props.settings);
            isReconnecting.value = false;
        }

        isSending.value = true;
        sendProgress.value = 0;

        await mqttService.sendMqttCommand('/command/idle', 'off');
        await mqttService.sendMqttCommand('/command/clearpage', 'all');
        await mqttService.sendMqttCommand('/command/config/gui', '{"idle1":100}');
        await mqttService.sendMqttCommand('/command/config/hasp', '{"hasp":{"theme":5}}');

        // Pre-collect all valid JSON lines so we know the total for progress
        const lines = localEditedText.value.split('\n');
        let buffer = '';
        const validLines = [];
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            buffer += trimmed;
            try {
                JSON.parse(buffer);
                validLines.push(buffer);
                buffer = '';
            } catch {
                // incomplete JSON, keep accumulating
            }
        }

        const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
        for (let i = 0; i < validLines.length; i++) {
            await mqttService.sendMqttCommand('/command/jsonl', validLines[i]);
            sendProgress.value = Math.round(((i + 1) / validLines.length) * 100);
            await delay(50);
        }
    } catch (error) {
        console.error('Failed to send to MQTT broker:', error);
    } finally {
        isSending.value = false;
        isReconnecting.value = false;
    }
}

const showDeployConfirm = ref(false);
const isDeploying = ref(false);
const deployStatus = ref('');

const confirmDeploy = () => {
    showDeployConfirm.value = false;
    deployToPlate();
};

// Deploy uploads pages.jsonl and nothing else, which is what the confirmation
// dialog promises. It deliberately does NOT gather images, fonts or audio:
// base64-encoding those on the main thread and pushing them through a single
// Tauri IPC call froze the renderer to a blank window on designs with any
// sizeable resource. Resources have their own transfer paths.
const deployToPlate = async () => {
    isDeploying.value = true;
    deployStatus.value = 'Uploading pages.jsonl...';

    try {
        const textBytes = new TextEncoder().encode(localEditedText.value);
        await uploadDeviceFile(props.settings.plate, 'L:/pages.jsonl', bytesToBase64(textBytes));

        deployStatus.value = `Deployed to ${props.settings.plate.name} successfully.`;
    } catch (error) {
        console.error('Failed to deploy:', error);
        deployStatus.value = `Deploy failed: ${error}`;
    } finally {
        isDeploying.value = false;
    }
};

const openModal = () => {
    isModalVisible.value = true; // Show modal when the button is clicked
};

const saveToFile = async (filename) => {
    if (!props.settings?.plate?.name) {
        console.error("Plate name is missing");
        return;
    }

    const now = new Date();
    const timestamp = now.toISOString().slice(2, 19).replace(/[-T:]/g, "");

    const baseFilename = filename || `${props.settings.plate.name}-${timestamp}`;

    let blob;
    let downloadName;
    try {
        const h3dArtifacts = await buildUsedH3dResourceArtifacts(props.layer);
        if (h3dArtifacts.length > 0) {
            const zip = new JSZip();
            zip.file('pages.jsonl', localEditedText.value);
            for (const artifact of h3dArtifacts) {
                zip.file(artifact.filename, artifact.bytes);
            }
            blob = await zip.generateAsync({ type: 'blob' });
            downloadName = `${baseFilename}.zip`;
        } else {
            blob = new Blob([localEditedText.value], { type: "application/jsonl" });
            downloadName = `${baseFilename}.jsonl`;
        }
    } catch (error) {
        console.error('Failed to export 3D model resources:', error);
        alert(error?.message || String(error));
        return;
    }

    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Helper function to sanitize the filename
const sanitizeFilename = (filename) => {
    // Replace invalid filename characters with underscores
    return filename.replace(/[\/:*?"<>|\\]/g, "_").trim();
};

</script>

<style scoped>
/* Add a style to highlight changed lines in the gutter */
.ace_changed-line {
    background-color: rgba(255, 0, 0, 0.3);
}

.custom-ace .ace_content {
    background-color: gray !important;
}
</style>
